import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import { COLORS } from '../../utils/constants';
import toast from 'react-hot-toast';
import {
  User, Mail, Phone, Shield, Calendar, CheckCircle2,
  Lock, Save, RotateCcw, ArrowRight, KeyRound, Sparkles
} from 'lucide-react';

const C = COLORS;

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    role: '',
  });
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || '',
        role: user.role || 'user',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      const changed =
        next.first_name !== (user?.first_name || '') ||
        next.last_name !== (user?.last_name || '') ||
        next.phone !== (user?.phone || '');
      setHasChanges(changed);
      return next;
    });
  };

  const handleReset = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || '',
        role: user.role || 'user',
      });
      setHasChanges(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.updateProfile({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone.trim(),
      });

      if (res.data?.user) {
        updateUser(res.data.user);
      } else {
        updateUser({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone.trim(),
        });
      }

      toast.success('Profile updated successfully!');
      setHasChanges(false);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Failed to update profile';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const initials = `${formData.first_name?.[0] || user?.first_name?.[0] || ''}${formData.last_name?.[0] || user?.last_name?.[0] || ''}`.toUpperCase() || 'U';

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Active Member';

  return (
    <AppLayout title="My Profile">
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* Top Hero Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 45%, #4338CA 100%)',
          borderRadius: 20,
          padding: '36px 32px',
          color: '#fff',
          boxShadow: '0 20px 25px -5px rgba(49, 46, 129, 0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 24,
          marginBottom: 28,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Subtle Decorative Circles */}
          <div style={{
            position: 'absolute', right: -40, top: -40, width: 220, height: 220,
            borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute', left: '30%', bottom: -60, width: 180, height: 180,
            borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 24, zIndex: 1 }}>
            <div style={{
              width: 84,
              height: 84,
              borderRadius: 22,
              background: 'linear-gradient(135deg, #6366F1, #818CF8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              fontWeight: 800,
              color: '#fff',
              boxShadow: '0 10px 20px rgba(0,0,0,0.25)',
              border: '3px solid rgba(255,255,255,0.2)',
              flexShrink: 0,
            }}>
              {initials}
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: -0.5 }}>
                  {formData.first_name} {formData.last_name}
                </h1>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: 'rgba(255,255,255,0.18)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  letterSpacing: 0.5,
                }}>
                  {user?.role || 'Admin'}
                </span>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 600,
                  background: 'rgba(16, 185, 129, 0.25)',
                  color: '#A7F3D0',
                  border: '1px solid rgba(16, 185, 129, 0.4)'
                }}>
                  <CheckCircle2 size={13} /> Active Account
                </span>
              </div>

              <p style={{ margin: '8px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Mail size={15} /> {formData.email}
              </p>
            </div>
          </div>

          <div style={{ zIndex: 1 }}>
            <button
              onClick={() => navigate('/change-password')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '11px 20px',
                borderRadius: 12,
                fontSize: 13.5,
                fontWeight: 600,
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.25)',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <KeyRound size={16} />
              Change Password
              <ArrowRight size={14} />
            </button>
          </div>
        </div>

        {/* Two Column Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          {/* Main Edit Form Card */}
          <div style={{
            background: '#fff',
            borderRadius: 18,
            border: `1px solid ${C.g200}`,
            padding: 28,
            boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.g100}` }}>
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: 'rgba(79, 70, 229, 0.08)',
                color: C.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <User size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.g900 }}>Personal Information</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12.5, color: C.g500 }}>Update your name, contact information, and public details.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
                    First Name <span style={{ color: C.danger }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter first name"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: `1.5px solid ${C.g200}`,
                      fontSize: 14,
                      color: C.g900,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = C.primary}
                    onBlur={(e) => e.target.style.borderColor = C.g200}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
                    Last Name <span style={{ color: C.danger }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter last name"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: `1.5px solid ${C.g200}`,
                      fontSize: 14,
                      color: C.g900,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = C.primary}
                    onBlur={(e) => e.target.style.borderColor = C.g200}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    style={{
                      width: '100%',
                      padding: '10px 14px 10px 38px',
                      borderRadius: 10,
                      border: `1.5px solid ${C.g200}`,
                      background: '#F9FAFB',
                      fontSize: 14,
                      color: C.g500,
                      boxSizing: 'border-box',
                      cursor: 'not-allowed',
                    }}
                  />
                  <Mail size={16} color={C.g400} style={{ position: 'absolute', left: 12, top: 13 }} />
                  <span style={{
                    position: 'absolute',
                    right: 12,
                    top: 10,
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 6,
                    background: C.g100,
                    color: C.g500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}>
                    <Lock size={11} /> Primary Login
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 11.5, color: C.g400 }}>
                  Email address cannot be changed directly as it is linked to your authentication credentials.
                </p>
              </div>

              <div style={{ marginBottom: 28 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
                  Phone Number
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number (optional)"
                    style={{
                      width: '100%',
                      padding: '10px 14px 10px 38px',
                      borderRadius: 10,
                      border: `1.5px solid ${C.g200}`,
                      fontSize: 14,
                      color: C.g900,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = C.primary}
                    onBlur={(e) => e.target.style.borderColor = C.g200}
                  />
                  <Phone size={16} color={C.g400} style={{ position: 'absolute', left: 12, top: 13 }} />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${C.g100}` }}>
                {hasChanges && (
                  <button
                    type="button"
                    onClick={handleReset}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '10px 18px',
                      borderRadius: 10,
                      border: `1px solid ${C.g200}`,
                      background: '#fff',
                      color: C.g700,
                      fontSize: 13.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <RotateCcw size={15} />
                    Discard Changes
                  </button>
                )}

                <button
                  type="submit"
                  disabled={loading || !hasChanges}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 24px',
                    borderRadius: 10,
                    border: 'none',
                    background: hasChanges ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})` : C.g300,
                    color: '#fff',
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: hasChanges && !loading ? 'pointer' : 'not-allowed',
                    boxShadow: hasChanges ? '0 4px 12px rgba(79, 70, 229, 0.25)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
          </div>

          {/* Side Info Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Account Details Card */}
            <div style={{
              background: '#fff',
              borderRadius: 18,
              border: `1px solid ${C.g200}`,
              padding: 24,
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.g900 }}>Account Details</h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: C.g500 }}>User ID</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.g800, fontFamily: 'monospace' }}>#{user?.id}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: C.g500 }}>Assigned Role</span>
                  <span style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    padding: '2px 8px',
                    borderRadius: 6,
                    background: 'rgba(79, 70, 229, 0.08)',
                    color: C.primary
                  }}>
                    {user?.role || 'Admin'}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: C.g500 }}>Account Status</span>
                  <span style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 6,
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: '#059669'
                  }}>
                    Active
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: C.g500 }}>Member Since</span>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: C.g700 }}>{memberSince}</span>
                </div>
              </div>
            </div>

            {/* Security Quick Link Card */}
            <div style={{
              background: '#fff',
              borderRadius: 18,
              border: `1px solid ${C.g200}`,
              padding: 24,
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: 'rgba(245, 158, 11, 0.1)',
                  color: '#D97706',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Shield size={18} />
                </div>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.g900 }}>Security Center</h4>
              </div>

              <p style={{ margin: '0 0 16px', fontSize: 12.5, color: C.g500, lineHeight: 1.5 }}>
                Ensure your account remains safe with regular password updates and session security.
              </p>

              <button
                type="button"
                onClick={() => navigate('/change-password')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '9px 14px',
                  borderRadius: 10,
                  border: `1.5px solid ${C.primary}`,
                  background: 'rgba(79, 70, 229, 0.04)',
                  color: C.primary,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(79, 70, 229, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(79, 70, 229, 0.04)'}
              >
                <KeyRound size={15} />
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default ProfilePage;
